import React, { useEffect, useReducer, createContext, Dispatch, useRef } from 'react';
import { KEY_ACCESS_TOKEN, KEY_REFRESH_TOKEN } from '../constant';
import { AuthAction, AuthState, AuthUser } from '../../interfaces/Contexts';
import { ReactChildren } from '../../interfaces/Common';
import storage from '../../storageService';
import {
  getBasicProfileSetting,
  Logout,
  registerFeedFeatureUser,
} from '../../Utils/apiService';
import Configurl from '../../Configurl';

const SIDE_NAV_DEFAULT_PROFILE_URL =
  'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT8oJh_Li1F9MTL485spsUw1L-cjntjj49vaCWWPZdjPoeZFH5lsCGjQlMZKwr1vNZUz6M&usqp=CAU';

// Initial State
export const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  search: null,
  isLoading: true,
};

// Reducer
const reducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN': {
      const userPayload: any = action.payload || { user: null };
      const accessTokenString =
        userPayload.user?.accessToken?.token?.token ||
        userPayload.user?.accessToken?.token ||
        '';
      const refreshTokenString =
        userPayload.user?.refreshToken?.token?.token ||
        userPayload.user?.refreshToken?.token ||
        '';

      const normalizedUser = userPayload.user
        ? {
            ...userPayload.user,
            accessToken: { token: accessTokenString },
            refreshToken: { token: refreshTokenString },
          }
        : null;

      // Save tokens and user to storage
      storage.set(KEY_ACCESS_TOKEN, accessTokenString);
      storage.set(KEY_REFRESH_TOKEN, refreshTokenString);
      storage.set('user', JSON.stringify(normalizedUser));
      localStorage.setItem('user', JSON.stringify(normalizedUser));

      return {
        ...state,
        isAuthenticated: !!normalizedUser,
        user: normalizedUser,
        isLoading: false,
      };
    }

    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        search: null,
        isLoading: false,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload?.isLoading ?? false,
      };

    case 'RESTORE_AUTH':
      return {
        ...state,
        isAuthenticated: !!action.payload?.user,
        user: action.payload?.user || null,
        isLoading: false,
      };

    default:
      return state;
  }
};
export const AuthContext = createContext<{
  state: AuthState;
  dispatch: Dispatch<AuthAction>;
  logoutUser: () => Promise<void>;
}>({
  state: initialState,
  dispatch: () => {},
  logoutUser: async () => {},
});

const toFeedUserName = (user: AuthUser) => {
  const source = user.applicantEmail?.split('@')[0] || user.applicantName || user.applicantId;
  const sanitized = source.toLowerCase().replace(/[^a-z0-9_]/g, '');
  return sanitized || user.applicantId;
};

const getFeedProfileUrl = async (user: AuthUser) => {
  try {
    const profileResponse = await getBasicProfileSetting(user.applicantId);
    const sideNavProfileUrl = profileResponse?.accountDetails?.Profile_Picture_URL__c;
    if (sideNavProfileUrl) return sideNavProfileUrl;
  } catch (error) {
    console.error('Failed to fetch profile picture for feed registration:', error);
  }

  return (
    user.Profile_Picture_URL__c ||
    user.applicationProfile ||
    SIDE_NAV_DEFAULT_PROFILE_URL ||
    Configurl.DefaultOrganizationLogo
  );
};

// Provider
export const AuthContextProvider: React.FunctionComponent<ReactChildren> = (props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const feedRegisteredUsersRef = useRef<Set<string>>(new Set());

  const logoutUser = async () => {
    try {
      const user = await storage.get('user');
      const parsedUser: AuthUser = user ? JSON.parse(user) : null;

      if (parsedUser?.applicantId) {
        const logoutTime = new Date().toISOString();

        await Logout({
          contactId: parsedUser.applicantId,
          logoutTime: logoutTime,
        });
      }

      await storage.remove(KEY_ACCESS_TOKEN);
      await storage.remove(KEY_REFRESH_TOKEN);
      await storage.remove('user');
      localStorage.removeItem('user');

      dispatch({ type: 'LOGOUT' });
    } catch (error) {
      console.error('Error while logging out:', error);
      dispatch({ type: 'LOGOUT' });
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: { isLoading: true } });
        await new Promise((resolve) => setTimeout(resolve, 100));

        const user = await storage.get('user');
        const accessToken = await storage.get(KEY_ACCESS_TOKEN);
        const refreshToken = await storage.get(KEY_REFRESH_TOKEN);

        if (user && accessToken && refreshToken) {
          try {
            const parsedUser: AuthUser = JSON.parse(user);
            if (parsedUser.accessToken?.token && parsedUser.refreshToken?.token) {
              dispatch({ type: 'RESTORE_AUTH', payload: { user: parsedUser } });
            } else {
              dispatch({ type: 'LOGOUT' });
            }
          } catch (parseError) {
            console.error('Failed to parse user data', parseError);
            dispatch({ type: 'LOGOUT' });
          }
        } else {
          dispatch({ type: 'RESTORE_AUTH', payload: { user: null } });
        }
      } catch (error) {
        console.error('Failed to fetch user data', error);
        dispatch({ type: 'RESTORE_AUTH', payload: { user: null } });
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const user = state.user;
    const accessToken = user?.accessToken?.token;
    if (!state.isAuthenticated || !user?.applicantId || !accessToken) return;

    let isCancelled = false;

    const registerUser = async () => {
      const profileUrl = await getFeedProfileUrl(user);
      if (isCancelled) return;

      const registrationKey = `feed-feature-registered-user-${user.applicantId}`;
      const registeredProfileUrlKey = `${registrationKey}-profile-url`;
      const registerAttemptKey = `${user.applicantId}-${profileUrl}`;
      const isSameProfileAlreadyRegistered =
        localStorage.getItem(registrationKey) &&
        localStorage.getItem(registeredProfileUrlKey) === profileUrl;

      if (feedRegisteredUsersRef.current.has(registerAttemptKey) || isSameProfileAlreadyRegistered) return;

      feedRegisteredUsersRef.current.add(registerAttemptKey);

      try {
        const response: any = await registerFeedFeatureUser(
          {
            userName: toFeedUserName(user),
            profileUrl,
          },
          accessToken
        );
        const isAlreadyRegistered =
          response?.messageId === 'VALIDATION_ERROR' &&
          String(response?.message || '').toLowerCase() ===
            'user already registered';
        const isSuccess = response?.success !== false || isAlreadyRegistered;
        // alert(`Feed feature registration verified. success: ${isSuccess}`);
        if (isSuccess) {
          localStorage.setItem(registrationKey, 'true');
          localStorage.setItem(registeredProfileUrlKey, profileUrl);
        } else {
          feedRegisteredUsersRef.current.delete(registerAttemptKey);
        }
      } catch (error) {
        feedRegisteredUsersRef.current.delete(registerAttemptKey);
        // alert('Feed feature registration verified. success: false');
        console.error('Feed feature user registration failed:', error);
      }
    };

    registerUser();

    return () => {
      isCancelled = true;
    };
  }, [state.isAuthenticated, state.user]);

  return (
    <AuthContext.Provider
      value={{
        state,
        dispatch,
        logoutUser,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
